// Kyte bag helpers — read-only offer summary + ancillaries-catalog normalizer.
// Kyte's pre-book bag flow runs through the unified ShopAncillaries call
// (kyte.ancillaries), not a standalone bags slice.

import { minor_to_decimal, as_number, as_array, as_boolean } from '@liveflight/providers/base/helpers'
import { currency_symbol } from '@resources'
import { get_kyte_ancillary_info } from './resources/ancillaries'
import { collect_traveler_ids } from './helpers'

const bag_quantity = (bag) => {
  if (!bag) return 0
  if (typeof bag !== 'object') return 0
  if (typeof bag.quantity === 'number' && bag.quantity > 0) return bag.quantity
  if (typeof bag.weight === 'number' && bag.weight > 0) return 1
  return 0
}

/**
 * Per-itinerary tally of cabin / included / chargeable bags for the first
 * segment plus a per-traveler breakdown. `extra` stays false on Kyte: extras
 * availability lives in the async ShopAncillaries catalog, not the offer.
 * @param {object} flight_offer
 * @param {number} [itinerary_index=0]
 * @returns {object}
 */
export function get_bags(flight_offer, itinerary_index = 0) {
  const parsed = {
    extra: false,
    travelers: [],
    cabin: 0,
    hand: 0,
    included: 0,
    chargeable: 0,
    total: 0,
  }

  if (!flight_offer) return parsed
  const { travelerPricings = [], itineraries = [] } = flight_offer
  if (itinerary_index < 0 || itinerary_index >= itineraries.length) return parsed

  const { segments: itinerary_segments = [] } = itineraries[itinerary_index] || {}
  if (itinerary_segments.length === 0) return parsed
  const first_segment_id = (itinerary_segments[0] || {}).id
  if (!first_segment_id) return parsed

  travelerPricings.forEach((traveler_pricing) => {
    if (!traveler_pricing) return
    const fare_detail = (traveler_pricing.fareDetailsBySegment || [])
      .find((f) => f && f.segmentId === first_segment_id)
    const traveler = {
      traveler_id: traveler_pricing.travelerId,
      traveler_type: traveler_pricing.traveler_type || traveler_pricing.travelerType,
      cabin: 0,
      hand: 0,
      included: 0,
      chargeable: 0,
    }

    if (fare_detail) {
      traveler.included = bag_quantity(fare_detail.includedCheckedBags)
      parsed.included += traveler.included

      traveler.cabin = bag_quantity(fare_detail.includedCabinBags)
      parsed.cabin += traveler.cabin

      traveler.hand = bag_quantity(fare_detail.includedHandBags)
      parsed.hand += traveler.hand

      const { additionalServices = [] } = fare_detail
      const list = Array.isArray(additionalServices) ? additionalServices : []
      traveler.chargeable = list
        .filter((entry) => entry && entry.type === 'bag')
        .reduce((sum, entry) => sum + bag_quantity(entry), 0)
      parsed.chargeable += traveler.chargeable
    }

    parsed.travelers.push(traveler)
  })

  parsed.total = parsed.included + parsed.chargeable
  return parsed
}

/**
 * True when the offer carries at least one chargeable bag for this traveler.
 * Without this override the base default (`false`) hides the CHECKED_BAGS
 * section on the confirm and checkout pages for Kyte offers.
 * @param {object} flight_offer
 * @param {string} traveler_id
 * @returns {boolean}
 */
export function has_chargeable_bags(flight_offer, traveler_id) {
  const { travelerPricings = [] } = flight_offer || {}
  const pricing = travelerPricings.find((t) => (t || {}).travelerId == traveler_id)
  const { fareDetailsBySegment = [] } = pricing || {}
  return fareDetailsBySegment.some((fdbs) => {
    const { additionalServices = [] } = fdbs || {}
    const list = Array.isArray(additionalServices) ? additionalServices : []
    return list.some((entry) => {
      const { type = '', quantity = 0 } = entry || {}
      return type === 'bag' && Number(quantity) > 0
    })
  })
}

export const build_extra_bags_catalog = (raw_catalog, flight_offer, provider, currency, type) => {
  const safe = raw_catalog || {}
  const ids = Object.keys(safe)
  if (!ids.length) return null

  const travelerIds = collect_traveler_ids(flight_offer)
  const safe_currency = currency || { code: 'EUR', decimals: 2 }
  const { code: currencyCode = 'EUR', decimals = 2 } = safe_currency
  const currencyGlyph = currency_symbol(currencyCode)
  const { providerId: provider_id = 'kyte' } = provider || {}
  const catalog = {}

  ids.forEach((bagId) => {
    const source = safe[bagId]
    if (!source) return
    const { segments = [], purchaseLimitations = {} } = source
    const total = minor_to_decimal(source.price, decimals)
    const info = get_kyte_ancillary_info(source, type)
    catalog[bagId] = {
      id: source.id,
      name: info.txt,
      subtitle: info.subtitle,
      icon: info.icon,
      // Catalog rows are 1 unit each; UI increments by 1 up to
      // purchaseQuantity (the max the upstream purchaseLimitations
      // allows for this ancillary).
      quantity: 1,
      purchaseQuantity: as_number(purchaseLimitations.purchaseQuantity, 1) || 1,
      incompatibleWith: as_array(purchaseLimitations.incompatibleWith),
      requiredOnAllSegments: as_boolean(purchaseLimitations.requiredOnAllSegments),
      combineWith: as_array(purchaseLimitations.combineWith),
      travelerIds,
      segmentIds: segments,
      bookableByItinerary: false,
      price: {
        total,
        grandTotal: total,
        base: total,
        taxes: [],
        currency: currencyCode || '',
        currencySymbol: currencyGlyph,
      },
      _provider: provider_id,
      _raw: source,
    }
  })

  return catalog
}